/**
 * View component for /files/new
 *
 * Creates a new file from a copy of the defaultItem in the file reducer
 */

// import primary libraries
import React from 'react'
import { connect } from 'react-redux'
import withRouter from 'react-router-dom/withRouter'

// import third-party libraries
import _update from 'lodash/update'
import _cloneDeep from 'lodash/cloneDeep'

// import moment from 'moment';
import { DateTime } from 'luxon'

// import actions
import * as requestTaskActions from '../requestTaskActions.js'
import * as activityActions from '../../activity/activityActions.js'
import * as requestActions from '../../request/requestActions.js'
import * as requestFolderActions from '../../requestFolder/requestFolderActions.js'

import { firmApi } from '../../../config/api/firm/firm.js'

// import global components
import Binder from '../../../global/components/Binder.jsx'
import Modal from '../../../global/components/modals/Modal.jsx'
import SingleDatePickerInput from '../../../global/components/forms/dates/SingleDatePickerInput.jsx'
import FileLocation from '../../file/components/FileLocation.jsx'
import UserInput from '../../../global/components/forms/UsersInput.jsx'
import CheckboxInput from '../../../global/components/forms/CheckboxInput.jsx'
import TextInput from '../../../global/components/forms/TextInput.jsx'
import SelectFromObject from '../../../global/components/forms/SelectFromObject.jsx'
import apiUtils from '../../../global/utils/api.js'
import { Info } from 'lucide-react'
import { Button } from '../../../global/components/ui/button.jsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../global/components/ui/tooltip.jsx'

// import components

class RequestTaskForm extends Binder {
  constructor(props) {
    super(props)
    this.state = {
      request: {},
      requestTask: {
        dueDate: DateTime.local().toMillis(),
        assignee: [],
      },
      submitting: false,
      field: {
        category: false,
        dueDate: false,
        description: false,
        assignee: false,
        _folder: false,
      },
      selectedFolder: {
        _client: props.match.params.clientId,
      },
      assignees: [
        {
          firstname: '',
          lastname: '',
          username: '',
          createContact: false,
        },
      ],
      // Request Template related state
      requestTemplates: [],
      selectedRequestTemplate: null,
      templateDocuments: [],
      selectedDocuments: [],
      specialInstructions: '',
      dateRange: { from: null, to: null },
      otherInputs: [''],
      perDocumentDates: {},
      perOtherInputDates: {},
    }
    this._bind(
      '_handleFormChange',
      '_handleAddAssignee',
      '_handleCreateRequestTask',
      '_handleClose',
      '_handleBulkUpdateRequesTask',
      '_handleCheckedField',
      '_handleLocationChange',
      '_handleUserChange',
      '_handleRemoveUser',
      '_handleRequestTemplateChange',
      '_handleDocumentSelection',
      '_handleDateRangeChange',
      '_handleSpecialInstructionsChange',
      '_handlePerDocumentDateChange',
      '_handleAddOtherInput',
      '_handleRemoveOtherInput',
      '_handleOtherInputChange',
      '_handlePerOtherInputDateChange',
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props
    const firmId = match.params.firmId

    dispatch(firmApi.endpoints.getFirmSettings.initiate(firmId))

    // Load request templates
    apiUtils
      .callAPI(`/api/firms/${parseInt(firmId)}/request-templates`, 'GET')
      .then(json => {
        if (json && json.data) {
          this.setState({ requestTemplates: json.data })
        }
      })
      .catch(err => {
        console.error('Failed to load request templates:', err)
      })
  }

  _handleFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */
    let newState = _update(_cloneDeep(this.state), e.target.name, () => {
      return e.target.value
    })
    this.setState(newState)
  }

  _handleAddAssignee() {
    const assignees = _cloneDeep(this.state.assignees)
    assignees.push({
      firstname: '',
      lastname: '',
      username: '',
      createContact: false,
    })
    this.setState({ assignees })
  }

  _handleCreateRequestTask() {
    const { dispatch, match, listArgs, selectedClient } = this.props
    const requestTask = _cloneDeep(this.state.requestTask)
    const selectedFolder = _cloneDeep(this.state.selectedFolder)
    const assignees = _cloneDeep(this.state.assignees)
    const {
      selectedRequestTemplate,
      selectedDocuments,
      specialInstructions,
      dateRange,
      otherInputs,
      perDocumentDates,
      perOtherInputDates,
    } = this.state

    let listArgsCopy = _cloneDeep(listArgs) || []

    this.setState({ submitting: true })

    const formatDate = date => {
      if (!date) return null
      const d = new Date(date)
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
    }

    const apiPayload = {
      // Core fields
      dueDate: requestTask.dueDate, // Keep as timestamp for dueDate
      assignee: [],
      category: requestTask.category,
      description: requestTask.description,
      _request: match.params.requestId,
      _firm: match.params.firmId,
      _client: selectedClient._id || null,
      _folder: selectedFolder && selectedFolder._id,
    }

    if (
      selectedRequestTemplate &&
      !selectedRequestTemplate.settings.date_range_enabled
    ) {
      apiPayload.dueDate = null
    }

    // Process assignees
    if (assignees && assignees.length) {
      assignees.forEach(({ emailError, ...item }) => {
        if (item && item.firstname && item.lastname && item.username) {
          apiPayload.assignee.push(item)
        }
      })
    }

    if (selectedRequestTemplate) {
      apiPayload.request_template_id = selectedRequestTemplate.id
      apiPayload.special_instructions = specialInstructions

      if (selectedRequestTemplate.settings.date_range_enabled) {
        apiPayload.documents_range_from_date = formatDate(dateRange.from)
        apiPayload.documents_range_to_date = formatDate(dateRange.to)
      }

      const documents = []

      selectedDocuments.forEach(docId => {
        documents.push({
          request_template_document_id: docId,
          custom_name: null,
          range_from_date: formatDate(perDocumentDates[docId]?.from) || null,
          range_to_date: formatDate(perDocumentDates[docId]?.to) || null,
        })
      })

      // Add "other" documents if enabled and provided
      if (selectedRequestTemplate.settings.other_input_enabled && otherInputs) {
        otherInputs.forEach((input, index) => {
          if (input && input.trim()) {
            documents.push({
              request_template_document_id: null,
              custom_name: input.trim(),
              range_from_date:
                formatDate(perOtherInputDates[index]?.from) || null,
              range_to_date: formatDate(perOtherInputDates[index]?.to) || null,
            })
          }
        })
      }

      apiPayload.documents = documents
    }

    dispatch(requestTaskActions.sendCreateRequestTask(apiPayload)).then(
      json => {
        if (json.success) {
          dispatch(
            requestActions.fetchSingleRequestById(match.params.requestId),
          )
          dispatch(requestTaskActions.addSingleRequestTaskToMap(json.item))
          dispatch(
            requestTaskActions.addRequestTaskToList(json.item, ...listArgsCopy),
          )
          dispatch(requestFolderActions.singleUpdateToMap(json.requestFolder))
          dispatch(requestActions.singleUpdateToMap(json.request))
          if (json.activity && selectedClient._id) {
            dispatch(
              activityActions.invalidateList(
                ...['_client', selectedClient._id],
              ),
            )
          }
          this._handleClose()
        } else {
          this.setState({ submitting: false })
          alert(json.error)
        }
      },
    )
  }

  _handleClose() {
    this.setState(
      {
        requestTask: {
          dueDate: null,
          assignee: [],
        },
        submitting: false,
        field: {
          category: false,
          dueDate: false,
          description: false,
          assignee: false,
          _folder: false,
        },
        assignees: [
          {
            firstname: '',
            lastname: '',
            username: '',
            createContact: false,
          },
        ],
        // Reset template-related state
        selectedRequestTemplate: null,
        templateDocuments: [],
        selectedDocuments: [],
        specialInstructions: '',
        dateRange: { from: null, to: null },
        otherInputs: [''],
        perDocumentDates: {},
        perOtherInputDates: {},
      },
      () => {
        this.props.close()
      },
    )
  }

  _handleBulkUpdateRequesTask() {
    const { dispatch, match, selectedTaskIds } = this.props
    const requestTask = _cloneDeep(this.state.requestTask)
    const assignees = _cloneDeep(this.state.assignees)
    const field = _cloneDeep(this.state.field)

    this.setState({ submitting: true })

    requestTask.dueDate = new Date(requestTask.dueDate)

    if (!field.category) {
      delete requestTask.category
    }
    if (!field.dueDate) {
      delete requestTask.dueDate
    }
    if (!field.description) {
      delete requestTask.description
    }
    if (!field.assignee) {
      delete requestTask.assignee
    } else {
      requestTask.assignee = []
      assignees.map(({ emailError, ...item }) => {
        if (item && item.firstname && item.lastname && item.username) {
          requestTask.assignee.push(item)
        }
      })
    }

    if (!field._folder) {
      delete requestTask._folder
    } else {
      requestTask._folder = this.state.selectedFolder?._id
    }

    const sendData = {
      requestTasksIds: selectedTaskIds,
      requestTask,
      _firm: match.params.firmId,
      _client: match.params.clientId,
      field,
    }

    dispatch(requestTaskActions.sendBulkUpdateRequestTask(sendData)).then(
      json => {
        if (json.success) {
          this._handleClose()
        } else {
          this.setState({ submitting: false })
          alert(json.error)
        }
      },
    )
  }

  _handleCheckedField(name) {
    const field = _cloneDeep(this.state.field)
    field[name] = !field[name]
    this.setState({ field })
  }

  _handleLocationChange(folder) {
    this.setState({ selectedFolder: folder })
  }

  _handleUserChange(e) {
    const { userMap } = this.props

    const user = userMap && e.target.value && userMap[e.target.value]
    if (user && user._id) {
      const assignee = {
        _id: user._id,
        firstname: user.firstname,
        lastname: user.lastname,
        username: user.username,
        createContact: false,
      }
      let newState = _update(_cloneDeep(this.state), e.target.name, () => {
        return assignee
      })
      this.setState(newState)
    }
  }

  _handleRemoveUser(index) {
    const assignees = _cloneDeep(this.state.assignees)
    delete assignees[index]
    this.setState({ assignees: assignees.filter(item => item) })
  }

  _handleRequestTemplateChange(e) {
    const { match } = this.props
    const firmId = match.params.firmId
    const templateId = e.target.value

    this.setState({
      requestTask: {
        dueDate: null,
        assignee: [],
      },
    })

    if (!templateId) {
      this.setState({
        selectedRequestTemplate: null,
        templateDocuments: [],
        selectedDocuments: [],
        specialInstructions: '',
        dateRange: { from: null, to: null },
        otherInputs: [''],
        perDocumentDates: {},
        perOtherInputDates: {},
      })
      return
    }

    // Load template details including documents
    apiUtils
      .callAPI(
        `/api/firms/${parseInt(firmId)}/request-templates/${parseInt(templateId)}`,
        'GET',
      )
      .then(json => {
        if (json && json.data) {
          this.setState({
            selectedRequestTemplate: json.data,
            templateDocuments: json.data.documents || [],
            selectedDocuments: [],
            specialInstructions: '',
            dateRange: { from: null, to: null },
            otherInputs: [''],
            perDocumentDates: {},
            perOtherInputDates: {},
          })
        }
      })
      .catch(err => {
        console.error('Failed to load template details:', err)
      })
  }

  _handleDocumentSelection(documentId) {
    const { selectedDocuments } = this.state
    let newSelectedDocuments = [...selectedDocuments]

    if (selectedDocuments.includes(documentId)) {
      newSelectedDocuments = selectedDocuments.filter(id => id !== documentId)
    } else {
      newSelectedDocuments.push(documentId)
    }

    this.setState({ selectedDocuments: newSelectedDocuments })
  }

  _handleDateRangeChange(field, e) {
    const dateRange = { ...this.state.dateRange }
    dateRange[field] = e.target.value
    this.setState({ dateRange })
  }

  _handleSpecialInstructionsChange(e) {
    this.setState({ specialInstructions: e.target.value })
  }

  _handlePerDocumentDateChange(documentId, type, e) {
    this.setState({
      perDocumentDates: {
        ...this.state.perDocumentDates,
        [documentId]: {
          ...this.state.perDocumentDates[documentId],
          [type]: e.target.value,
        },
      },
    })
  }

  _handleAddOtherInput() {
    const otherInputs = [...this.state.otherInputs]
    otherInputs.push('')
    this.setState({ otherInputs })
  }

  _handleRemoveOtherInput(index) {
    const otherInputs = [...this.state.otherInputs]
    const perOtherInputDates = { ...this.state.perOtherInputDates }

    otherInputs.splice(index, 1)
    // Remove the corresponding date entry
    delete perOtherInputDates[index]

    // Reindex the remaining dates to match the new array indices
    const reindexedDates = {}
    Object.keys(perOtherInputDates).forEach(oldIndex => {
      const newIndex =
        parseInt(oldIndex) > index ? parseInt(oldIndex) - 1 : parseInt(oldIndex)
      if (newIndex >= 0) {
        reindexedDates[newIndex] = perOtherInputDates[oldIndex]
      }
    })

    // Ensure at least one input remains
    if (otherInputs.length === 0) {
      otherInputs.push('')
    }

    this.setState({ otherInputs, perOtherInputDates: reindexedDates })
  }

  _handleOtherInputChange(index, e) {
    const otherInputs = [...this.state.otherInputs]
    otherInputs[index] = e.target.value
    this.setState({ otherInputs })
  }

  _handlePerOtherInputDateChange(index, type, e) {
    this.setState({
      perOtherInputDates: {
        ...this.state.perOtherInputDates,
        [index]: {
          ...this.state.perOtherInputDates[index],
          [type]: e.target.value,
        },
      },
    })
  }

  render() {
    const {
      isOpen,
      selectedClient = {},
      clientUserListItems = [],
      selectedRequestTask,
      isBulkEditMode,
      match,
      isRequestTemplatesEnabled,
    } = this.props

    const {
      submitting,
      requestTask = {},
      field,
      selectedFolder,
      assignees,
      requestTemplates,
      selectedRequestTemplate,
      templateDocuments,
      selectedDocuments,
      specialInstructions,
      dateRange,
      otherInputs,
      perDocumentDates,
      perOtherInputDates,
    } = this.state
    const modalHeaderText = isBulkEditMode
      ? 'Update Request Tasks'
      : 'Create Request Task'
    const modalConfirmtext = submitting
      ? isBulkEditMode
        ? 'Updating...'
        : 'Creating...'
      : isBulkEditMode
        ? 'Update Request Tasks'
        : 'Create Request Task'
    let disabledButton =
      !(requestTask.category && requestTask.description) ||
      assignees.some(assignee => assignee.emailError)

    // Additional validation for template requirements
    if (selectedRequestTemplate) {
      // If template has documents, at least one must be selected OR other inputs must be provided
      if (
        templateDocuments &&
        templateDocuments.length > 0 &&
        selectedDocuments.length === 0 &&
        (!selectedRequestTemplate.settings.other_input_enabled ||
          !otherInputs.some(input => input && input.trim()))
      ) {
        disabledButton = true
      }
      // If template has no predefined documents but other input is enabled, at least one other input is required
      if (
        (!templateDocuments || templateDocuments.length === 0) &&
        selectedRequestTemplate.settings.other_input_enabled &&
        !otherInputs.some(input => input && input.trim())
      ) {
        disabledButton = true
      }
    }

    if (isBulkEditMode) {
      disabledButton = false
      if (
        field.category ||
        field.dueDate ||
        field.description ||
        field.assignee ||
        field._folder
      ) {
        if (field.assignee && assignees.some(assignee => assignee.emailError)) {
          disabledButton = true
        } else if (
          field.category &&
          !(
            requestTask &&
            requestTask.category &&
            requestTask.category.trim().length
          )
        ) {
          disabledButton = true
        } else if (field.dueDate) {
          disabledButton = false
        } else if (
          field.description &&
          !(
            requestTask &&
            requestTask.description &&
            requestTask.description.trim().length
          )
        ) {
          disabledButton = true
        } else if (field._folder || field.assignee) {
          disabledButton = false
        }
      } else {
        disabledButton = true
      }
    }

    return (
      <Modal
        cardSize="large"
        isOpen={isOpen}
        closeAction={this._handleClose}
        closeText="Cancel"
        confirmAction={
          isBulkEditMode
            ? this._handleBulkUpdateRequesTask
            : this._handleCreateRequestTask
        }
        confirmText={modalConfirmtext}
        disableConfirm={submitting || disabledButton}
        modalHeader={modalHeaderText}
        showButtons={true}
      >
        <div>
          <div className="-share-link-configuration">
            <div className="-header">
              <i className="fas fa-eye" /> Link settings
            </div>
            <div className="-body">
              {(selectedClient._id && (
                <div className="-setting yt-row space-between">
                  <div className="-instructions yt-col">
                    {isBulkEditMode ? (
                      <CheckboxInput
                        // disabled={!checked}
                        label="Upload Location"
                        name="requestTask"
                        value={field._folder}
                        change={this._handleCheckedField.bind(this, '_folder')}
                        checked={field._folder}
                      />
                    ) : (
                      <p>
                        <strong>Upload Location</strong>
                      </p>
                    )}
                  </div>
                  <div className="-inputs yt-col">
                    {/* <TextInput
                                            change={this._handleFormChange}
                                            name="requestTask._folder"
                                            placeholder="Enter category"
                                            value={requestTask._folder}
                                            disabled={!field._folder && isBulkEditMode}
                                        /> */}
                    <FileLocation
                      selectedClient={selectedClient}
                      handleLocationChange={this._handleLocationChange}
                      allowCreateFolder={true} // (firm.allowCreateFolder && viewingAs === "portal") || (viewingAs !== "portal")}
                      selectedFolder={selectedFolder}
                      personalId={null}
                      // getDetail={{ type: "workspace", id: selectedClient && selectedClient._id, firmId: match.params.firmId }}
                      getDetail={{
                        type: (selectedClient._id && 'workspace') || 'general',
                        id: selectedClient._id,
                        firmId: match.params.firmId,
                      }}
                      action="move"
                    />
                  </div>
                </div>
              )) ||
                null}
              {(selectedClient._id && <hr />) || null}

              {/* Request Template Dropdown */}
              {!isBulkEditMode && isRequestTemplatesEnabled && (
                <>
                  <div className="-setting yt-row space-between">
                    <div className="-instructions yt-col">
                      <p>
                        <strong>Select Type of Engagement</strong>
                      </p>
                    </div>
                    <div className="-inputs yt-col">
                      <SelectFromObject
                        change={this._handleRequestTemplateChange}
                        name="requestTemplate"
                        placeholder="-- Select Template --"
                        items={requestTemplates}
                        display="name"
                        value="id"
                        selected={
                          selectedRequestTemplate
                            ? selectedRequestTemplate.id.toString()
                            : ''
                        }
                      />
                    </div>
                  </div>
                  <hr />
                </>
              )}

              {/* Conditional Fields Based on Template Settings */}
              {selectedRequestTemplate && selectedRequestTemplate.settings && (
                <>
                  {/* Date Range Section */}
                  {selectedRequestTemplate.settings.date_range_enabled && (
                    <>
                      <div className="-setting yt-row space-between">
                        <div className="-instructions yt-col">
                          <p>
                            <strong>Specify Ranges for Documents</strong>
                          </p>
                        </div>
                        <div
                          className="-inputs yt-col"
                          style={{ display: 'flex', gap: '10px' }}
                        >
                          <div style={{ flex: 1 }}>
                            <label
                              style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#666',
                              }}
                            >
                              From
                            </label>
                            <SingleDatePickerInput
                              change={e =>
                                this._handleDateRangeChange('from', e)
                              }
                              name="dateRange.from"
                              placeholder="Pick a date"
                              initialDate={dateRange.from}
                              inputClasses="-right"
                              numberOfMonths={1}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label
                              style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#666',
                              }}
                            >
                              To
                            </label>
                            <SingleDatePickerInput
                              change={e => this._handleDateRangeChange('to', e)}
                              name="dateRange.to"
                              placeholder="Pick a date"
                              initialDate={dateRange.to}
                              inputClasses="-right"
                              numberOfMonths={1}
                            />
                          </div>
                        </div>
                      </div>
                      <hr />
                    </>
                  )}
                </>
              )}

              <div className="-setting yt-row space-between">
                <div className="-instructions yt-col">
                  {isBulkEditMode ? (
                    <CheckboxInput
                      // disabled={!checked}
                      label="Category"
                      name="requestTask"
                      value={field.category}
                      change={this._handleCheckedField.bind(this, 'category')}
                      checked={field.category}
                    />
                  ) : (
                    <p>
                      <strong>
                        Category<sup className="-required">*</sup>
                      </strong>
                    </p>
                  )}
                </div>
                <div className="-inputs yt-col">
                  <TextInput
                    change={this._handleFormChange}
                    name="requestTask.category"
                    placeholder="Enter category"
                    value={requestTask.category}
                    disabled={!field.category && isBulkEditMode}
                  />
                </div>
              </div>
              <hr />

              {(!selectedRequestTemplate ||
                (selectedRequestTemplate &&
                  selectedRequestTemplate.settings &&
                  selectedRequestTemplate.settings.due_date_enabled !==
                    false)) && (
                <>
                  <div className="-setting yt-row space-between">
                    <div className="-instructions yt-col">
                      {isBulkEditMode ? (
                        <CheckboxInput
                          // disabled={!checked}
                          label="Due Date"
                          name="requestTask"
                          value={field.dueDate}
                          change={this._handleCheckedField.bind(
                            this,
                            'dueDate',
                          )}
                          checked={field.dueDate}
                        />
                      ) : (
                        <p>
                          <strong>Due Date</strong>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="tw-ml-1 tw-h-6 tw-w-6 tw-align-middle"
                                aria-label="About the due date"
                              >
                                <Info aria-hidden="true" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Select an optional due date. Upon due date
                              expiration, the request link is deactivated.
                            </TooltipContent>
                          </Tooltip>
                        </p>
                      )}
                    </div>
                    <div className="-inputs yt-col">
                      <SingleDatePickerInput
                        anchorDirection="right" // This aligns the calendar drop down to the right side of the date-input. Default is to the left.
                        change={this._handleFormChange}
                        enableOutsideDays={false}
                        initialDate={requestTask.dueDate} // epoch/unix time in milliseconds
                        inputClasses="-right"
                        minDate={
                          !!selectedRequestTask && !!selectedRequestTask.dueDate
                            ? new Date(selectedRequestTask.dueDate).getTime()
                            : DateTime.local().toMillis()
                        }
                        name="requestTask.dueDate"
                        numberOfMonths={1}
                        placeholder="Set due date"
                        disabled={!field.dueDate && isBulkEditMode}
                      />
                    </div>
                  </div>
                  <hr />
                </>
              )}
              <div className="-setting yt-row space-between">
                <div className="-instructions yt-col">
                  {isBulkEditMode ? (
                    <CheckboxInput
                      // disabled={!checked}
                      label="Description"
                      name="requestTask"
                      value={field.description}
                      change={this._handleCheckedField.bind(
                        this,
                        'description',
                      )}
                      checked={field.description}
                    />
                  ) : (
                    <p>
                      <strong>
                        Description<sup className="-required">*</sup>
                      </strong>
                    </p>
                  )}
                </div>
                <div className="-inputs yt-col">
                  <TextInput
                    change={this._handleFormChange}
                    name="requestTask.description"
                    placeholder="Enter description"
                    value={requestTask.description}
                    disabled={!field.description && isBulkEditMode}
                  />
                </div>
              </div>

              {/* Template-specific fields */}
              {selectedRequestTemplate && selectedRequestTemplate.settings && (
                <>
                  {/* Select Files to Request */}
                  {((templateDocuments && templateDocuments.length > 0) ||
                    selectedRequestTemplate.settings.other_input_enabled) && (
                    <>
                      <hr />
                      <div className="-setting yt-row space-between">
                        <div className="-instructions yt-col">
                          <p>
                            <strong>
                              Select Files to Request
                              {templateDocuments &&
                                templateDocuments.length > 0 && (
                                  <sup className="-required">*</sup>
                                )}
                            </strong>
                          </p>
                        </div>
                        <div className="-inputs yt-col">
                          {templateDocuments && templateDocuments.length > 0 ? (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns:
                                  'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '8px',
                              }}
                            >
                              {templateDocuments.map(doc => (
                                <div key={doc.id}>
                                  <CheckboxInput
                                    label={doc.name}
                                    name={`document-${doc.id}`}
                                    value={selectedDocuments.includes(doc.id)}
                                    checked={selectedDocuments.includes(doc.id)}
                                    change={() =>
                                      this._handleDocumentSelection(doc.id)
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ fontStyle: 'italic', color: '#666' }}>
                              No predefined documents available for this
                              template.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Other Input - Add custom document names */}
                      {selectedRequestTemplate.settings.other_input_enabled && (
                        <div
                          className="-setting yt-row space-between"
                          style={{ marginTop: '16px' }}
                        >
                          <div className="-instructions yt-col">
                            <p>
                              <strong>Add Other Documents</strong>
                            </p>
                          </div>
                          <div className="-inputs yt-col">
                            <div className="input-group">
                              {otherInputs.map((input, index) => (
                                <div
                                  key={index}
                                  style={{
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <TextInput
                                    change={e =>
                                      this._handleOtherInputChange(index, e)
                                    }
                                    name={`otherInput-${index}`}
                                    placeholder="Enter custom document name"
                                    value={input}
                                    style={{ flex: 1 }}
                                  />
                                  {otherInputs.length > 1 && (
                                    <button
                                      type="button"
                                      className="yt-btn x-small danger"
                                      onClick={() =>
                                        this._handleRemoveOtherInput(index)
                                      }
                                      style={{
                                        padding: '6px 8px',
                                        minWidth: 'auto',
                                      }}
                                    >
                                      <i className="fal fa-trash" />
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="yt-btn xx-small"
                                onClick={this._handleAddOtherInput}
                                style={{ marginTop: '8px' }}
                              >
                                <i className="fal fa-plus" /> Add another
                                document
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Per Document Dates */}
                  {selectedRequestTemplate.settings.per_document_dates &&
                    (selectedDocuments.length > 0 ||
                      otherInputs.some(input => input && input.trim())) && (
                      <>
                        <hr />
                        <div className="-setting yt-row space-between">
                          <div className="-instructions yt-col">
                            <p>
                              <strong>Document Specific Dates</strong>
                            </p>
                          </div>
                          <div className="-inputs yt-col">
                            {/* Template Documents */}
                            {selectedDocuments.map(docId => {
                              const doc = templateDocuments.find(
                                d => d.id === docId,
                              )

                              if (!doc) {
                                return null
                              }

                              return (
                                <div
                                  key={docId}
                                  style={{ marginBottom: '10px' }}
                                >
                                  <label
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#666',
                                    }}
                                  >
                                    {doc.name}
                                  </label>
                                  <div
                                    className="-inputs yt-col"
                                    style={{ display: 'flex', gap: '10px' }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <label
                                        style={{
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          color: '#666',
                                        }}
                                      >
                                        From
                                      </label>
                                      <SingleDatePickerInput
                                        change={e =>
                                          this._handlePerDocumentDateChange(
                                            docId,
                                            'from',
                                            e,
                                          )
                                        }
                                        name={`perDocumentDate-${docId}-from`}
                                        placeholder="Pick a date"
                                        initialDate={
                                          perDocumentDates[docId]?.from || null
                                        }
                                        inputClasses="-right"
                                        numberOfMonths={1}
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label
                                        style={{
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          color: '#666',
                                        }}
                                      >
                                        To
                                      </label>
                                      <SingleDatePickerInput
                                        change={e =>
                                          this._handlePerDocumentDateChange(
                                            docId,
                                            'to',
                                            e,
                                          )
                                        }
                                        name={`perDocumentDate-${docId}-to`}
                                        placeholder="Pick a date"
                                        initialDate={
                                          perDocumentDates[docId]?.to || null
                                        }
                                        inputClasses="-right"
                                        numberOfMonths={1}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            })}

                            {/* Other Input Documents */}
                            {otherInputs.map((input, index) => {
                              // Only show date picker for non-empty inputs
                              if (!input || !input.trim()) return null

                              return (
                                <div
                                  key={index}
                                  style={{ marginBottom: '10px' }}
                                >
                                  <label
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      color: '#666',
                                    }}
                                  >
                                    {input.trim()}
                                  </label>
                                  <div
                                    className="-inputs yt-col"
                                    style={{ display: 'flex', gap: '10px' }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <label
                                        style={{
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          color: '#666',
                                        }}
                                      >
                                        From
                                      </label>
                                      <SingleDatePickerInput
                                        change={e =>
                                          this._handlePerOtherInputDateChange(
                                            index,
                                            'from',
                                            e,
                                          )
                                        }
                                        name={`perOtherInputDate-${index}-from`}
                                        placeholder="Pick a date"
                                        initialDate={
                                          perOtherInputDates[index]?.from ||
                                          null
                                        }
                                        inputClasses="-right"
                                        numberOfMonths={1}
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <label
                                        style={{
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          color: '#666',
                                        }}
                                      >
                                        To
                                      </label>
                                      <SingleDatePickerInput
                                        change={e =>
                                          this._handlePerOtherInputDateChange(
                                            index,
                                            'to',
                                            e,
                                          )
                                        }
                                        name={`perOtherInputDate-${index}-to`}
                                        placeholder="Pick a date"
                                        initialDate={
                                          perOtherInputDates[index]?.to || null
                                        }
                                        inputClasses="-right"
                                        numberOfMonths={1}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </>
                    )}

                  {/* Special Instructions */}
                  <hr />
                  <div className="-setting yt-row space-between">
                    <div className="-instructions yt-col">
                      <p>
                        <strong>Special Instructions</strong>
                      </p>
                    </div>
                    <div className="-inputs yt-col">
                      <textarea
                        value={specialInstructions}
                        onChange={this._handleSpecialInstructionsChange}
                        placeholder="Include number of documents, specify if it needs to be signed, give specific dates or years, etc"
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          lineHeight: '1.4',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {(selectedClient._id && <hr />) || null}
              <div className="-setting yt-row space-between">
                <div className="-instructions yt-col">
                  {isBulkEditMode ? (
                    <CheckboxInput
                      // disabled={!checked}
                      label="Assignee"
                      name="requestTask"
                      value={field.assignee}
                      change={this._handleCheckedField.bind(this, 'assignee')}
                      checked={field.assignee}
                    />
                  ) : (
                    <p>
                      <strong>Assignee</strong>
                    </p>
                  )}
                </div>
                <div className="yt-col">
                  <button
                    className="yt-btn xx-small u-pullRight input-group"
                    onClick={this._handleAddAssignee}
                  >
                    <i className="fal fa-plus" /> Add assignees
                  </button>
                  <div className="input-group">
                    {assignees.map((assignee, i) => {
                      return assignee ? (
                        <UserInput
                          allowSharedEmail={true}
                          change={this._handleFormChange}
                          handleUserChange={this._handleUserChange}
                          currentIndex={i}
                          key={'assignee' + i}
                          userListItems={clientUserListItems}
                          user={assignee}
                          selectedClient={
                            (match.params.clientId && selectedClient) || null
                          }
                          usersId={assignees.flatMap(item =>
                            item._id ? [Number(item._id)] : [],
                          )}
                          fieldName="assignees"
                          users={assignees}
                          removeUser={this._handleRemoveUser}
                        />
                      ) : null
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }
}

RequestTaskForm.propTypes = {}

RequestTaskForm.defaultProps = {}

const mapStoreToProps = (store, props) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  // const availableStaff = !staffList ? [] : staffList.filter(staff => {
  //     if (staff.status === 'active') {
  //       let item = staff;
  //       let fullName = userStore.byId[staff._user] ? `${userStore.byId[staff._user].firstname} ${userStore.byId[staff._user].lastname}` : '';
  //       let userName = userStore.byId[staff._user] ? userStore.byId[staff._user].username : '';
  //       item.displayName = `${fullName} | ${userName}`;
  //       item.email = userName;
  //       return item;
  //     }
  //   });
  const { selectedUsers = [], match } = props
  const firmId = Number(match.params.firmId)
  const filteredUsers = selectedUsers.filter(user => {
    return !(
      user.portal_disabled_firms && user.portal_disabled_firms.includes(firmId)
    )
  })

  const clientUserListItems = filteredUsers.map(user => {
    let newUser = _cloneDeep(user)
    if (!user.firstname && !user.lastname) {
      newUser.displayName = user.username
    } else {
      newUser.displayName = `${user.firstname} ${user.lastname}`
    }
    newUser.email = user.username
    return newUser
  })

  const firmSettings = firmApi.endpoints.getFirmSettings.select(
    match.params.firmId,
  )(store)?.data
  const isRequestTemplatesEnabled =
    firmSettings?.featureFlags?.REQUEST_TEMPLATES || false

  return {
    loggedInUser: store.user.loggedIn.user,
    clientUserListItems,
    isRequestTemplatesEnabled,
  }
}

export default withRouter(connect(mapStoreToProps)(RequestTaskForm))
